import { makeAutoObservable } from 'mobx';
import type { MessagingService } from '@backgroundTasks/services/MessagingService/types';
import ImportTask, { type OnFinishedCallback, type OnAssetImportedCallback } from '../../models/ImportTask';

export type AssetsImportServiceParams = {
	messagingService: MessagingService,
};

export default class AssetsImportService {
	messagingService: MessagingService;

	imports: ImportTask[];

	constructor( { messagingService }: AssetsImportServiceParams ) {
		this.messagingService = messagingService;

		this.imports = [];

		makeAutoObservable( this );
	}

	get unfinishedImportsCount() {
		return this.unfinishedImports.length;
	}

	get hasUnfinishedImports() {
		return this.unfinishedImportsCount > 0;
	}

	get totalAssetsToImport() {
		return this.unfinishedImports.accumulate( importTask => importTask.total );
	}

	get totalAssetsImported() {
		return this.unfinishedImports.accumulate( importTask => importTask.count );
	}

	get allImportsSucceeded() {
		return this.imports.every( importTask => importTask.status === 'successful' );
	}

	getImport( importID: string ) {
		return this.imports.find( importTask => importTask.id === importID );
	}

	registerAssetsImport(
		id: string,
		onAssetImported?: OnAssetImportedCallback,
		onFinished?: OnFinishedCallback
	) {
		const importTask = ImportTask.createAndObserve( {
			id,
			messagingService: this.messagingService,
			onAssetImported,
			onFinished
		} );

		this.imports.push( importTask );
	}

	cancelAllImports() {
		this.unfinishedImports.forEach( importTask => importTask.cancel() );
	}

	private get unfinishedImports() {
		return this.imports.filter( importTask => !importTask.hasFinished );
	}
}
